import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { 
  FiCpu, 
  FiCode, 
  FiGitBranch, 
  FiTrendingUp, 
  FiShield,
  FiRefreshCw,
  FiDatabase 
} from 'react-icons/fi';
import { 
  SiOpenai, 
  SiGithubactions, 
  SiKubernetes 
} from 'react-icons/si';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.7);
  }
  70% {
    transform: scale(1.1);
    box-shadow: 0 0 0 10px rgba(0, 0, 0, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  }
`;

const ProcessContainer = styled.section`
  padding: 8rem 2rem;
//   background: linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%);
//   color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
  }
`;

const GlowingOrb = styled.div`
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: radial-gradient(circle at center, ${props => props.color}33 0%, transparent 70%);
  filter: blur(60px);
  z-index: 0;
  animation: float 10s ease-in-out infinite;

  &.orb1 { top: -100px; left: -100px; }
  &.orb2 { bottom: -100px; right: -100px; }
`;

const SectionTitle = styled.h2`
  font-size: 3.5rem;
  text-align: center;
  margin-bottom: 1.5rem;
  color: black;
  font-weight: 800;
  background: linear-gradient(135deg, #fff 0%, #f0f0f0 100%);
  -webkit-background-clip: text;
//   -webkit-text-fill-color: transparent;
  position: relative;
`;

const SectionSubtitle = styled.p`
  text-align: center;
  color: #636e72;
  font-size: 1.1rem;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ProcessSteps = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 0;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(
      to bottom,
      transparent,
      rgba(255, 255, 255, 0.2) 15%,
      rgba(255, 255, 255, 0.2) 85%,
      transparent
    );
    transform: translateX(-50%);
  }

  @media (max-width: 768px) {
    &::before {
      left: 30px;
    }
  }
`;

const ProcessStep = styled.div`
  display: flex;
  justify-content: ${props => props.index % 2 === 0 ? 'flex-start' : 'flex-end'};
  align-items: center;
  margin: 2rem 0;
  opacity: 0;
  visibility: hidden;
  animation: ${fadeInUp} 1s ease-out forwards;
  animation-delay: ${props => `${props.index * 0.3}s`};
  
  ${props => props.isVisible && css`
    visibility: visible;
  `}
  
  @media (max-width: 768px) {
    justify-content: flex-start;
    margin-left: 60px;
  }
`;

const StepContent = styled.div`
  width: 44%;
  position: relative;
  padding: 2rem;
  background: ${props => `${props.color}10`};
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid ${props => `${props.color}30`};
  transition: all 0.5s ease;
  cursor: pointer;
  opacity: 0;
  transform: translateY(20px);
  
  ${props => props.isVisible && css`
    opacity: 1;
    transform: translateY(0);
    transition: opacity 1s ease-out, transform 1s ease-out;
  `}

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    ${props => props.index % 2 === 0 ? 'right: -15px' : 'left: -15px'};
    transform: translateY(-50%);
    border-style: solid;
    border-width: 15px;
    border-color: transparent;
    ${props => props.index % 2 === 0 
      ? `border-left-color: ${props.color}10;`
      : `border-right-color: ${props.color}10;`
    }
  }

  &:hover {
    transform: translateY(-5px);
    background: ${props => `${props.color}20`};
    border-color: ${props => `${props.color}50`};
    box-shadow: 0 5px 15px ${props => `${props.color}20`};
  }

  @media (max-width: 768px) {
    width: 100%;
    
    &::before {
      left: -15px;
      border-right-color: ${props => `${props.color}10`};
      border-left-color: transparent;
    }
  }
`;

const StepNumber = styled.div`
  width: 60px;
  height: 60px;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    padding: 2px;
    background: linear-gradient(45deg, ${props => props.color}, transparent);
    -webkit-mask: 
      linear-gradient(#fff 0 0) content-box, 
      linear-gradient(#fff 0 0);
    mask: 
      linear-gradient(#fff 0 0) content-box, 
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: ${props => `${props.color}10`};
    backdrop-filter: blur(4px);
  }

  svg {
    font-size: 1.8rem;
    color: ${props => props.color};
    filter: drop-shadow(0 0 8px ${props => `${props.color}66`});
    z-index: 1;
    transition: all 0.3s ease;
  }

  ${props => props.isHovered && css`
    &::before {
      animation: ${spin} 4s linear infinite;
    }
    
    svg {
      animation: ${spin} 1s ease-out;
      transform: scale(1.2);
      filter: drop-shadow(0 0 12px ${props => `${props.color}99`});
    }
  `}

  @media (max-width: 768px) {
    left: -60px;
  }
`;

const StepTitle = styled.h3`
  font-size: 1.25rem;
  color: #2d3436;
  margin-bottom: 1rem;
  text-align: center;
`;

const StepDescription = styled.p`
  color: #636e72;
  text-align: center;
  line-height: 1.6;
`;

const SubPoints = styled.ul`
  margin-top: 1.5rem;
  padding-left: 0;
  list-style: none;

  li {
    color: black;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    font-size: 0.9rem;

    &::before {
      content: '•';
      color: ${props => props.color};
      margin-right: 0.5rem;
      font-size: 1.2rem;
    }
  }
`;

const ProcessSection = () => {
  const [hoveredIndex, setHoveredIndex] = React.useState(null);
  const [visibleSteps, setVisibleSteps] = React.useState([]);
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const [isInView, setIsInView] = React.useState(false);
  const sectionRef = React.useRef(null);

  // Intersection Observer setup
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Wait 1 second after entering viewport before starting animations
          setTimeout(() => {
            setIsInView(true);
          }, 1000);
          observer.disconnect();
        }
      },
      {
        threshold: 0.2 // Trigger when 20% of the section is visible
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Modified useEffect to slow down the sequential animations
  React.useEffect(() => {
    if (isInView && isInitialLoad) {
      const showStepsSequentially = () => {
        steps.forEach((_, index) => {
          setTimeout(() => {
            setVisibleSteps(prev => [...prev, index]);
          }, 2000 * index); // Increased from 1000 to 2000ms (2 seconds) between each step
        });
      };

      // Add initial delay before starting the sequence
      setTimeout(() => {
        showStepsSequentially();
        setIsInitialLoad(false);
      }, 500); // Half second delay before starting the sequence
    }
  }, [isInView, isInitialLoad]);

  const steps = [
    {
      icon: <FiCpu />,
      title: "AI-Powered Discovery & Business Analysis",
      description: "Leverage AI to analyze requirements, predict challenges, and generate optimal project roadmaps using self-developed advanced machine learning algorithms & LLM.",
      color: "#FF6B6B",
      subPoints: [
        "Deep learning powered requirement analysis",
        "Predictive risk assessment",
        "Automated resource allocation",
        "Strategic timeline generation"
      ]
    },
    {
      icon: <FiDatabase />,
      title: "Intelligent Architecture Design",
      description: "Utilize the latest infra architecture patterns and cloud-native solutions to design scalable, future-proof systems.",
      color: "#4ECDC4",
      subPoints: [
        "AI-optimized infrastructure: avoid resource waste from the start",
        "Serverless-first approach: never overprovisioning",
        "Microservices architecture: agility & scalability for your business",
        "Auto-scaling design patterns: real-time dynamic scaling in terms of different scenarios"
      ]
    },
    {
      icon: <FiCode />,
      title: "Agile Development & DevOps",
      description: "Implement CI/CD with AI-assisted coding, automated testing, and intelligent code review processes.",
      color: "#45B7D1",
      subPoints: [
        "Continuous integration, testing, and deployment",
        "AI-assisted code review",
        "Automated code optimization",
      ]
    },
    {
      icon: <FiShield />,
      title: "AI Security & Quality Assurance",
      description: "Employ AI-driven security testing and automated QA processes to ensure robust, secure solutions.",
      color: "#96CEB4",
      subPoints: [
        "ML-based security testing",
        "Automated vulnerability detection",
        "Smart load testing",
        "AI-powered user testing"
      ]
    },
    {
      icon: <FiGitBranch />,
      title: "Smart Deployment & Scaling",
      description: "Deploy using containerization and orchestration with AI-optimized performance monitoring.",
      color: "#4834D4",
      subPoints: [
        "Kubernetes orchestration",
        "Auto-scaling infrastructure",
        "AI performance optimization",
        "Smart monitoring & alerts"
      ]
    },
    {
      icon: <FiRefreshCw />,
      title: "Iterative Enhancement",
      description: "Use AI feedback loops and user behavior analysis to continuously enhance and optimize the solution.",
      color: "#A55EEA",
      subPoints: [
        "User behavior analysis",
        "AI-driven improvements",
        "Automated A/B testing",
        "Smart feature rollouts"
      ]
    }
  ];

  return (
    <ProcessContainer ref={sectionRef}>
      {isInView && (
        <>
          {/* <GlowingOrb color="#FF6B6B" className="orb1" />
          <GlowingOrb color="#4ECDC4" className="orb2" /> */}
          
          <SectionTitle>How We Work</SectionTitle>
          <SectionSubtitle>
            Our proven development process ensures successful project delivery
            through systematic planning, execution, and quality control.
          </SectionSubtitle>
          
          <ProcessSteps>
            {steps.map((step, index) => (
              <ProcessStep 
                key={index} 
                index={index}
                isVisible={visibleSteps.includes(index)}
              >
                <StepNumber 
                  color={step.color}
                  isHovered={hoveredIndex === index}
                  isVisible={visibleSteps.includes(index)}
                >
                  {step.icon}
                </StepNumber>
                <StepContent 
                  index={index} 
                  color={step.color}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  isVisible={visibleSteps.includes(index)}
                >
                  <StepTitle>{step.title}</StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                  <SubPoints color={step.color}>
                    {step.subPoints.map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                  </SubPoints>
                </StepContent>
              </ProcessStep>
            ))}
          </ProcessSteps>
        </>
      )}
    </ProcessContainer>
  );
};

export default ProcessSection; 